import React, { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { GhostButton } from 'src/components/widgets/buttons';
import { Dialog, DialogWindow } from 'src/components/widgets/dialog';
import { FormControl } from 'src/components/widgets/formControl';
import { AdvancedSelect } from 'src/components/widgets/select';
import Stack from 'src/components/widgets/stack/stack';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { T, stringFormat } from 'src/shared/helpers';
import type { ColorAttribute } from './helpers';
import VariantMappingReplacementChoices from './variantMappingReplacementChoices';
import { describeValueReplacementConflict } from './variantMappingRepairMessages';
import type { ChannelMappingRepair } from './variantMappingRepair';
import { buildChannelRepairPreview, type ChannelRepairPreview } from './variantMappingRepairPreview';
import { replacementKey } from './variantMappingRepairStrategies/manual';
import type { AttributeReplacement, RepairStrategyID } from './variantMappingRepairStrategies/types';

const RepairDialogWindow = styled(DialogWindow)`
    box-sizing: border-box;
    max-height: calc(100dvh - 48px);
    max-width: 100%;
    padding: 24px;
    overflow: hidden;

    > h1,
    > div:last-child {
        flex-shrink: 0;
    }

    > h1 + div {
        display: flex;
        overflow: hidden;
    }
`;

const ContentStack = styled(Stack)`
    flex: 1;
    min-height: 0;
    min-width: 0;

    > * {
        flex-shrink: 0;
    }

    & > & {
        flex-shrink: 1;
    }
`;

const ReplacementControls = styled.div`
    max-height: 40dvh;
    overflow-y: auto;
    overscroll-behavior: contain;

    &:empty {
        display: none;
    }
`;

const Preview = styled.div`
    isolation: isolate;

    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
    }
    th,
    td {
        padding: 8px 12px;
        text-align: left;
        vertical-align: top;
        border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
        overflow-wrap: anywhere;
    }
    th {
        position: sticky;
        top: 0;
        background: white;
        z-index: 1;
    }
    th:first-child {
        width: 22%;
    }
`;

const StrategyList = styled(Stack)`
    flex-wrap: wrap;
    align-items: center;
`;

const StrategyTag = styled.span`
    padding: 4px 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 4px;
    font-size: 12px;
`;

const CountButton = styled(GhostButton)`
    min-width: 0;
    text-decoration: underline;

    &[aria-pressed='true'] {
        font-weight: 600;
    }
`;

const getStrategyDetails = (id: RepairStrategyID) => {
    switch (id) {
        case 'shopify-option-order':
            return {
                label: T._('Shopify option reorder', 'Admin'),
                description: T._('Match options by name after a swap, such as Size / Color to Color / Size. Values stay with their option.', 'Admin')
            };
        case 'shopify-option-positions':
            return {
                label: T._('Shopify option shifts / removals', 'Admin'),
                description: T._(
                    'Update option positions, such as 2 to 1, and remove deleted options only when the remaining combination is unambiguous.',
                    'Admin'
                )
            };
        case 'value-rename':
            return {
                label: T._('Renamed values', 'Admin'),
                description: T._(
                    'Update values renamed in your store, such as Royal blue to Royal. Review the suggested matches before applying the repairs.',
                    'Admin'
                )
            };
        case 'manual-selection':
            return {
                label: T._('Selected replacement', 'Admin'),
                description: T._('Use the replacement you selected for this original value in this sales channel.', 'Admin')
            };
        case 'casing':
            return {
                label: T._('Match store casing', 'Admin'),
                description: T._('Match the capitalization of store option names, value labels and IDs, such as RED to Red.', 'Admin')
            };
    }
};

const StrategyBadges: FC<{ strategies: RepairStrategyID[] }> = ({ strategies }) => (
    <StrategyList $horizontal $gap='4px'>
        {strategies.map((id) => {
            const { label, description } = getStrategyDetails(id);
            return (
                <ZTooltip key={id} title={description}>
                    <StrategyTag aria-label={description}>{label}</StrategyTag>
                </ZTooltip>
            );
        })}
    </StrategyList>
);

const formatLabel = (label: string, id: string) => (label === String(id) ? label : `${label} (${id})`);

const MappingPreview: FC<{ attributes: ColorAttribute[] }> = ({ attributes }) => (
    <Stack $gap='4px'>
        {attributes.map((attribute, index) => (
            <div key={index}>{`${formatLabel(attribute.Label, attribute.Id)}: ${formatLabel(attribute.Value.Label, attribute.Value.Id)}`}</div>
        ))}
    </Stack>
);

const ChangesTable: FC<{ repairs: ChannelRepairPreview['repairable'] }> = ({ repairs }) => (
    <table>
        <thead>
            <tr>
                <th scope='col'>{T._('Variant', 'Admin')}</th>
                <th scope='col'>{T._('Before', 'Admin')}</th>
                <th scope='col'>{T._('After', 'Admin')}</th>
                <th scope='col'>{T._('Strategy', 'Admin')}</th>
            </tr>
        </thead>
        <tbody>
            {repairs.map((repair) => (
                <tr key={repair.key}>
                    <td>{repair.name}</td>
                    <td>
                        <MappingPreview attributes={repair.before} />
                    </td>
                    <td>
                        <MappingPreview attributes={repair.after} />
                    </td>
                    <td>
                        <StrategyBadges strategies={repair.steps.map((step) => step.strategyID)} />
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ManualChangesTable: FC<{ repairs: ChannelRepairPreview['manualRepairs'] }> = ({ repairs }) => (
    <table>
        <thead>
            <tr>
                <th scope='col'>{T._('Variant', 'Admin')}</th>
                <th scope='col'>{T._('Current options', 'Admin')}</th>
                <th scope='col'>{T._('Reason', 'Admin')}</th>
            </tr>
        </thead>
        <tbody>
            {repairs.map((repair) => (
                <tr key={repair.key}>
                    <td>{repair.name}</td>
                    <td>{repair.before.length ? <MappingPreview attributes={repair.before} /> : T._('Invalid options', 'Admin')}</td>
                    <td>
                        {repair.reason === 'value-conflict'
                            ? repair.valueConflicts?.length
                                ? repair.valueConflicts.map(describeValueReplacementConflict).join(' ')
                                : T._('Different original values would use the same store value. Choose distinct replacements.', 'Admin')
                            : repair.reason === 'duplicate'
                              ? T._('Another variant would use the same combination. Choose a different replacement.', 'Admin')
                              : repair.reason === 'invalid'
                                ? T._('The saved options cannot be read. Use the variant edit button to correct them.', 'Admin')
                                : T._('Choose replacement values to repair this variant.', 'Admin')}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ExpandedPreview = styled(Preview)`
    max-height: min(480px, 50dvh);
    overflow: auto;
    overscroll-behavior: contain;
`;

const VariantsList = styled(Preview)`
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;

    ${ContentStack} > & {
        flex: 1 1 auto;
    }
`;

const allChannelsValue = 'all-sales-channels';

const RepairVariantMappingsDialog: FC<{
    channels: ChannelMappingRepair[];
    allOptionsReady: boolean;
    onConfirm: (channels: ChannelMappingRepair[], allChannels: boolean) => void;
}> = ({ channels, allOptionsReady, onConfirm }) => {
    const hasMultipleChannels = channels.length > 1;
    const [replacementsByChannel, setReplacementsByChannel] = useState<Record<string, readonly AttributeReplacement[]>>({});
    const [selectedChannel, setSelectedChannel] = useState(hasMultipleChannels ? allChannelsValue : channels[0].salesChannelID);
    const [showIssues, setShowIssues] = useState(true);
    const [expandedPreview, setExpandedPreview] = useState<{ channelID: string; manual: boolean } | null>(null);
    const togglePreview = (channelID: string, manual?: boolean) => {
        const showManual = manual ?? previews.some((preview) => preview.channel.salesChannelID === channelID && preview.manualRepairs.length > 0);
        setExpandedPreview((current) => (current?.channelID === channelID && current.manual === showManual ? null : { channelID, manual: showManual }));
    };
    const selectChannel = (channelID: string) => {
        setSelectedChannel(channelID);
        setExpandedPreview(null);
        setShowIssues(true);
    };
    const manualExplanation = T._('Choose the updated store values in any empty dropdowns. Variants with unresolved issues will remain unchanged.', 'Admin');
    const previews = useMemo(
        () => channels.map((channel) => buildChannelRepairPreview(channel, replacementsByChannel[channel.salesChannelID] ?? [])),
        [channels, replacementsByChannel]
    );
    const replacementChoices = (channelID: string) => {
        const preview = previews.find((candidate) => candidate.channel.salesChannelID === channelID);
        return preview ? (
            <ReplacementControls>
                <VariantMappingReplacementChoices
                    candidates={preview.replacementCandidates}
                    onChange={(key, replacement) => {
                        setReplacementsByChannel((current) => {
                            const others = (current[channelID] ?? []).filter((value) => replacementKey(value.optionID, value.valueID) !== key);
                            return { ...current, [channelID]: replacement ? [...others, replacement] : others };
                        });
                        setShowIssues(true);
                        setExpandedPreview((current) => (current?.channelID === channelID ? { ...current, manual: true } : current));
                    }}
                />
            </ReplacementControls>
        ) : null;
    };
    const { channel, repairable, manualRepairs, strategyPreviews } =
        previews.find((preview) => preview.channel.salesChannelID === selectedChannel) ?? previews[0];
    const allChannels = hasMultipleChannels && selectedChannel === allChannelsValue;
    const showIssueList = manualRepairs.length > 0 && (showIssues || repairable.length === 0);
    const repairableChannels = previews.filter((preview) => preview.repairable.length > 0);
    const hasManualRepairs = previews.some((preview) => preview.manualRepairs.length > 0);
    const totalRepairs = repairableChannels.reduce((total, preview) => total + preview.repairable.length, 0);
    const readyCount = allChannels ? totalRepairs : repairable.length;
    const channelOptions = [
        { value: allChannelsValue, label: T._('All sales channels', 'Admin') },
        ...previews.map(({ channel }) => ({
            value: channel.salesChannelID,
            label: stringFormat(T._('{0} - {1} variants with mismatches', 'Admin'), channel.label, channel.repairs.length.toString())
        }))
    ];
    return (
        <Dialog
            title={T._('Repair variants', 'Admin')}
            size='large'
            windowDecorator={RepairDialogWindow}
            buttons={[
                {
                    label:
                        readyCount === 0
                            ? T._('Apply repairs', 'Admin')
                            : allChannels
                              ? stringFormat(
                                    T._('Repair {0} variants across {1} channels', 'Admin'),
                                    totalRepairs.toString(),
                                    repairableChannels.length.toString()
                                )
                              : stringFormat(T._('Repair {0} variants', 'Admin'), repairable.length.toString()),
                    disabled: readyCount === 0 || (allChannels && !allOptionsReady),
                    onClick: () => onConfirm(allChannels ? previews.map((preview) => preview.channel) : [channel], allChannels)
                }
            ]}
        >
            <ContentStack $gap='12px'>
                {hasMultipleChannels && (
                    <FormControl label={T._('Sales channel', 'Admin')}>
                        <AdvancedSelect
                            options={channelOptions}
                            value={channelOptions.find((option) => option.value === selectedChannel)}
                            onChange={(option) => option && selectChannel(option.value)}
                            menuPosition='fixed'
                        />
                    </FormControl>
                )}
                {allChannels ? (
                    <ContentStack $gap='12px'>
                        {!allOptionsReady && (
                            <div>
                                {T._(
                                    'Some sales channels have not been checked. Finish loading or retry failed channels, then reopen this dialog to repair all channels.',
                                    'Admin'
                                )}
                            </div>
                        )}
                        <VariantsList>
                            <table>
                                <thead>
                                    <tr>
                                        <th scope='col'>{T._('Sales channel', 'Admin')}</th>
                                        <th scope='col'>{T._('Strategies to apply', 'Admin')}</th>
                                        <th scope='col'>{T._('Ready to apply', 'Admin')}</th>
                                        {hasManualRepairs && (
                                            <th scope='col'>
                                                <ZTooltip title={manualExplanation}>
                                                    <span>{T._('Needs attention', 'Admin')}</span>
                                                </ZTooltip>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previews.map((preview) => (
                                        <React.Fragment key={preview.channel.salesChannelID}>
                                            <tr>
                                                <td>
                                                    <GhostButton $size='small' onClick={() => togglePreview(preview.channel.salesChannelID)}>
                                                        {preview.channel.label}
                                                    </GhostButton>
                                                </td>
                                                <td>
                                                    {preview.strategyPreviews.length > 0 ? (
                                                        <StrategyBadges strategies={preview.strategyPreviews} />
                                                    ) : (
                                                        T._('Review replacements', 'Admin')
                                                    )}
                                                </td>
                                                <td>
                                                    {preview.repairable.length > 0 ? (
                                                        <CountButton
                                                            type='button'
                                                            $size='small'
                                                            aria-label={stringFormat(
                                                                T._('View changes to {0} variants for {1}', 'Admin'),
                                                                preview.repairable.length.toString(),
                                                                preview.channel.label
                                                            )}
                                                            aria-expanded={
                                                                expandedPreview?.channelID === preview.channel.salesChannelID && !expandedPreview.manual
                                                            }
                                                            onClick={() => togglePreview(preview.channel.salesChannelID, false)}
                                                        >
                                                            {preview.repairable.length}
                                                        </CountButton>
                                                    ) : (
                                                        0
                                                    )}
                                                </td>
                                                {hasManualRepairs && (
                                                    <td>
                                                        {preview.manualRepairs.length > 0 ? (
                                                            <CountButton
                                                                type='button'
                                                                $size='small'
                                                                aria-label={stringFormat(
                                                                    T._('View {0} variants needing attention for {1}', 'Admin'),
                                                                    preview.manualRepairs.length.toString(),
                                                                    preview.channel.label
                                                                )}
                                                                aria-expanded={
                                                                    expandedPreview?.channelID === preview.channel.salesChannelID && expandedPreview.manual
                                                                }
                                                                onClick={() => togglePreview(preview.channel.salesChannelID, true)}
                                                            >
                                                                {preview.manualRepairs.length}
                                                            </CountButton>
                                                        ) : (
                                                            0
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                            {expandedPreview?.channelID === preview.channel.salesChannelID && (
                                                <tr>
                                                    <td colSpan={hasManualRepairs ? 4 : 3}>
                                                        <Stack $gap='8px'>
                                                            <strong>{stringFormat(T._('Preview for {0}', 'Admin'), preview.channel.label)}</strong>
                                                            {replacementChoices(preview.channel.salesChannelID)}
                                                            <ExpandedPreview>
                                                                {expandedPreview.manual && preview.manualRepairs.length > 0 ? (
                                                                    <>
                                                                        <div>{manualExplanation}</div>
                                                                        <ManualChangesTable repairs={preview.manualRepairs} />
                                                                    </>
                                                                ) : (
                                                                    <ChangesTable repairs={preview.repairable} />
                                                                )}
                                                            </ExpandedPreview>
                                                        </Stack>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </VariantsList>
                        {hasManualRepairs && <div>{manualExplanation}</div>}
                        <div>
                            {T._(
                                'Select a variant count to expand its preview. Repairs still apply to all sales channels. Save the product after repairing.',
                                'Admin'
                            )}
                        </div>
                    </ContentStack>
                ) : (
                    <ContentStack $gap='12px'>
                        {replacementChoices(channel.salesChannelID)}
                        {strategyPreviews.length > 0 && (
                            <StrategyList $horizontal $gap='8px'>
                                <strong>{T._('Strategies to apply:', 'Admin')}</strong>
                                <StrategyBadges strategies={strategyPreviews} />
                            </StrategyList>
                        )}
                        {manualRepairs.length > 0 && (
                            <StrategyList $horizontal $gap='12px'>
                                <CountButton type='button' $size='small' aria-pressed={showIssueList} onClick={() => setShowIssues(true)}>
                                    {stringFormat(T._('List {0} remaining variants with issues', 'Admin'), manualRepairs.length.toString())}
                                </CountButton>
                                {repairable.length > 0 && (
                                    <CountButton type='button' $size='small' aria-pressed={!showIssueList} onClick={() => setShowIssues(false)}>
                                        {stringFormat(T._('Preview repairs for {0} variants', 'Admin'), repairable.length.toString())}
                                    </CountButton>
                                )}
                            </StrategyList>
                        )}
                        {showIssueList && <div>{manualExplanation}</div>}
                        <VariantsList key={`${selectedChannel}-${showIssueList ? 'issues' : 'repairs'}`}>
                            {showIssueList ? <ManualChangesTable repairs={manualRepairs} /> : <ChangesTable repairs={repairable} />}
                        </VariantsList>
                        <div>
                            {hasMultipleChannels
                                ? T._('Applies to the selected channel only. Save the product after repairing.', 'Admin')
                                : T._('Save the product after repairing.', 'Admin')}
                        </div>
                    </ContentStack>
                )}
            </ContentStack>
        </Dialog>
    );
};

export default RepairVariantMappingsDialog;
